import { Grid, Stack } from '@careflux/primitives';
import { HorizontalBarChart } from './HorizontalBarChart';
import { MetricTile } from './MetricTile';
import { QueryState } from './QueryState';
import { TrendChart } from './TrendChart';
import type { RegenerationOverviewProps, TabProps } from './components.types';
import { formatNumber, formatPercent, formatRatio } from './formatting';
import type { Regeneration } from './types';
import { useSummaryRegeneration, useSummaryTimeline } from './useSummaryQualityQueries';

const TRIGGER_LABELS: Record<keyof Regeneration['byTrigger'], string> = {
  style: 'Stíl breytt',
  template: 'Sniðmáti breytt',
  retry: 'Reynt aftur',
};

const RegenerationOverview = ({ regeneration }: RegenerationOverviewProps) => (
  <Stack gap="lg">
    <Grid columns={{ base: 1, md: 2, xl: 4 }} gap="md">
      <MetricTile
        title="Endurmyndunarhlutfall"
        value={formatPercent(regeneration.regenRate)}
        detail="Lotur með fleiri en eina samantekt"
      />
      <MetricTile
        title="Samantektir á lotu"
        value={formatRatio(regeneration.avgGenerationsPerAudio)}
        detail="Meðalfjöldi"
      />
      <MetricTile title="Lotur" value={formatNumber(regeneration.audios)} />
      <MetricTile title="Samantektir" value={formatNumber(regeneration.generations)} />
    </Grid>
    <HorizontalBarChart
      title="Ástæður endurmyndunar"
      description="Fjöldi endurmyndana eftir kveikju"
      data={(Object.keys(TRIGGER_LABELS) as (keyof Regeneration['byTrigger'])[]).map((trigger) => ({
        label: TRIGGER_LABELS[trigger],
        value: regeneration.byTrigger[trigger],
      }))}
      valueLabel="Endurmyndanir"
      formatValue={formatNumber}
    />
  </Stack>
);

export const RegenerationTab = ({ filters, bucket, onBucketChange }: TabProps) => {
  const regenerationQuery = useSummaryRegeneration(filters);
  const timelineQuery = useSummaryTimeline(filters, bucket);

  return (
    <Stack gap="lg">
      <QueryState
        isLoading={regenerationQuery.isLoading}
        isError={regenerationQuery.isError}
        error={regenerationQuery.error}
        onRetry={() => void regenerationQuery.refetch()}
        loadingLabel="Sæki endurmyndanir"
        errorTitle="Endurmyndanir hlóðust ekki"
      />
      {regenerationQuery.data && <RegenerationOverview regeneration={regenerationQuery.data} />}

      <QueryState
        isLoading={timelineQuery.isLoading}
        isError={timelineQuery.isError}
        error={timelineQuery.error}
        onRetry={() => void timelineQuery.refetch()}
        loadingLabel="Sæki tímalínu"
        errorTitle="Tímalína hlóðst ekki"
      />
      {timelineQuery.data && (
        <TrendChart
          title="Endurmyndunarhlutfall yfir tíma"
          description="Hlutfall lota með fleiri en eina samantekt"
          points={timelineQuery.data}
          series={[{ dataKey: 'regenRate', label: 'Endurmyndun', color: 'hsl(var(--chart-1))' }]}
          formatValue={(value) => formatPercent(value, 0)}
          bucket={bucket}
          onBucketChange={onBucketChange}
        />
      )}
    </Stack>
  );
};
